import type { ReactElement } from 'react';

/**
 * 파일 유형 그림 — 브랜드 아이콘 세트 (2026-08-18).
 *
 * 지금까지 워크스페이스 파일 아이콘은 lucide 선 아이콘에 유형별 **색만** 달리 준 것이라
 * (FileText 파랑=Word · FileText 빨강=PDF) 같은 그림이 색으로만 갈렸다. 목록에서 Word 와
 * PDF 를 한눈에 가르기 어렵고, 한글(.hwp)은 Word 와 그림이 겹쳤다.
 *
 * 그래서 유형마다 **다른 그림**을 쓴다. 원본은 Microsoft 가 OneDrive·Office 웹에서 쓰는
 * item-type 32px 세트(`res.public.onecdn.static.microsoft/…/assets/item-types/32/*.svg`)이며,
 * 사용자가 지정한 것이다. 흰 문서지 + 유형 그림 + 좌하단 배지라는 한 가족의 문법을 공유해
 * 16px 까지 줄여도 무엇인지 읽힌다.
 *
 * **한글(.hwp/.hwpx)만 직접 그렸다** — 한컴 로고는 래스터(webp)로만 구할 수 있어 그대로
 * 쓰면 확대·다크에서 뭉갠다. 같은 문서지 위에 한컴 마크(하늘색 패널 + 4단 밴드)를 얹고,
 * 배지에 흰 `ㅎ` 을 넣어 나머지 15종과 같은 규격으로 맞췄다.
 *
 * ⚠ **gradient id 는 유형 이름으로 접두**한다(`gword-a`). 원본은 전부 `a`·`b`·`c` 라
 *   한 화면에 두 종류가 같이 뜨면 `url(#a)` 가 **먼저 그려진 쪽**을 가리켜 Word 아이콘이
 *   Excel 색으로 칠해진다. 같은 유형이 여러 개 뜨는 것은 정의가 같아 무해하다.
 *
 * ⚠ **크기는 유형마다 다시 맞춘다** — 원본이 32 상자 안에서 저마다 다른 크기로 그려져
 *   있어 그대로 쓰면 같은 size 를 줘도 아이콘이 크고 작다. 아래 `VIEW_BOX` 참조.
 *
 * ⚠ **색을 상속하지 않는다.** 브랜드 색이 그림 안에 박혀 있어 부모의 `color` 는 무시된다
 *   (선 아이콘은 currentColor 를 따랐다). 유형색이 필요한 자리(필터 칩 등)는 계속 선
 *   아이콘을 쓴다 — 이 파일은 **파일 목록의 유형 표시** 전용이다.
 */

/** 그림이 있는 유형. 여기 없는 유형은 호출부가 기존 선 아이콘으로 물러난다. */
export type FileGlyphName =
  | 'folder'
  | 'word'
  | 'excel'
  | 'csv'
  | 'ppt'
  | 'hwp'
  | 'pdf'
  | 'markup'
  | 'html'
  | 'code'
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'archive'
  | 'other';

/** 유형 → 그림. 원본 SVG 를 그대로 인라인한 것(요청 0회·오프라인 동작). */
const GLYPHS: Record<FileGlyphName, ReactElement> = {
  /** 폴더 */
  folder: (
    <>
    <path fill="url(#gfolder-a)" d="M11.525 5.025A3.5 3.5 0 0 0 9.05 4H5.5A3.5 3.5 0 0 0 2 7.5v16A3.5 3.5 0 0 0 5.5 27h21a3.5 3.5 0 0 0 3.5-3.5v-13A3.5 3.5 0 0 0 26.5 7H14.121a1.5 1.5 0 0 1-1.06-.44l-1.536-1.535z" />
    <path fill="url(#gfolder-b)" fillOpacity=".2" d="M11.525 5.025A3.5 3.5 0 0 0 9.05 4H5.5A3.5 3.5 0 0 0 2 7.5v16A3.5 3.5 0 0 0 5.5 27h21a3.5 3.5 0 0 0 3.5-3.5v-13A3.5 3.5 0 0 0 26.5 7H14.121a1.5 1.5 0 0 1-1.06-.44l-1.536-1.535z" />
    <path fill="url(#gfolder-c)" d="M3 12.5A2.5 2.5 0 0 1 5.5 10h5.25a2.5 2.5 0 0 0 2-1 2.5 2.5 0 0 1 2-1H26.5a2.5 2.5 0 0 1 2.5 2.5v13a2.5 2.5 0 0 1-2.5 2.5h-21A2.5 2.5 0 0 1 3 23.5v-11z" />
    <defs>
    <linearGradient id="gfolder-a" x1="9" x2="9" y1="26" y2="4" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BA7300" />
    <stop offset=".221" stopColor="#C77B00" />
    <stop offset=".716" stopColor="#D48300" />
    <stop offset="1" stopColor="#D48300" />
    </linearGradient>
    <radialGradient id="gfolder-b" cx="0" cy="0" r="1" gradientTransform="matrix(0 2.92121 -10 0 12 10.5)" gradientUnits="userSpaceOnUse">
    <stop />
    <stop offset="1" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="gfolder-c" x1="14.25" x2="10.375" y1="26.25" y2="8" gradientUnits="userSpaceOnUse">
    <stop stopColor="#F7AB40" />
    <stop offset="1" stopColor="#FFDD57" />
    </linearGradient>
    </defs>
    </>
  ),
  /** Word (.docx/.doc/.docm) */
  word: (
    <>
    <path fill="url(#gword-a)" d="M5 5a3 3 0 0 1 3-3h13a6 6 0 0 1 6 6v19a3 3 0 0 1-3 3H11a6 6 0 0 1-6-6V5z" />
    <path fill="url(#gword-b)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gword-c)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gword-d)" d="M22 17v5.531A1.47 1.47 0 0 1 20.531 24H15v-3a4.001 4.001 0 0 0-2.973-3.866L15.571 13 22 17z" />
    <path fill="url(#gword-e)" d="M22 17.031a1.47 1.47 0 0 1-1.469 1.469h-6.41A3.991 3.991 0 0 0 11 17h-1v-2.3a1.89 1.89 0 0 1 1.878-1.89l8.855-.053L22 12v5.031z" />
    <path fill="url(#gword-f)" fillOpacity=".6" d="M22 17.031a1.47 1.47 0 0 1-1.469 1.469h-6.41A3.991 3.991 0 0 0 11 17h-1v-2.3a1.89 1.89 0 0 1 1.878-1.89l8.855-.053L22 12v5.031z" />
    <path fill="url(#gword-g)" fillOpacity=".1" d="M22 17.031a1.47 1.47 0 0 1-1.469 1.469h-6.41A3.991 3.991 0 0 0 11 17h-1v-2.3a1.89 1.89 0 0 1 1.878-1.89l8.855-.053L22 12v5.031z" />
    <path fill="url(#gword-h)" d="M20.531 8A1.47 1.47 0 0 1 22 9.469v2.562a1.47 1.47 0 0 1-1.469 1.469h-8.076A2.455 2.455 0 0 0 10 15.955V10.52A2.519 2.519 0 0 1 12.519 8h8.012z" />
    <path fill="url(#gword-i)" fillOpacity=".8" d="M20.531 8A1.47 1.47 0 0 1 22 9.469v2.562a1.47 1.47 0 0 1-1.469 1.469h-8.076A2.455 2.455 0 0 0 10 15.955V10.52A2.519 2.519 0 0 1 12.519 8h8.012z" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gword-j)" rx="3" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gword-k)" fillOpacity=".65" rx="3" />
    <path fill="#fff" d="m11.7 21-1.508 7H8.39l-.89-4.2-.932 4.2h-1.82L3.3 21h1.486l.894 4.62.888-4.62H8.39l.93 4.62.872-4.62H11.7z" />
    <defs>
    <linearGradient id="gword-a" x1="12.104" x2="16.851" y1="2" y2="29.972" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gword-b" x1="11.556" x2="15.786" y1="3" y2="29.035" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gword-c" cx="0" cy="0" r="1" gradientTransform="matrix(10.5556 20.5833 -15.8333 7.71875 12.389 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <radialGradient id="gword-d" cx="0" cy="0" r="1" gradientTransform="matrix(27.5042 0 0 8.52474 21.416 24)" gradientUnits="userSpaceOnUse">
    <stop offset=".18" stopColor="#1657F4" />
    <stop offset=".575" stopColor="#0036C4" />
    </radialGradient>
    <linearGradient id="gword-e" x1="10.107" x2="19.174" y1="21.094" y2="21.094" gradientUnits="userSpaceOnUse">
    <stop stopColor="#66C0FF" />
    <stop offset=".256" stopColor="#0094F0" />
    </linearGradient>
    <radialGradient id="gword-f" cx="0" cy="0" r="1" gradientTransform="matrix(-7.98381 15.2097 -17.5489 -41.5814 21.946 13.745)" gradientUnits="userSpaceOnUse">
    <stop offset=".14" stopColor="#D471FF" />
    <stop offset=".831" stopColor="#509DF5" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="gword-g" cx="0" cy="0" r="1" gradientTransform="matrix(0 13.3025 -40.1142 0 20.214 24.117)" gradientUnits="userSpaceOnUse">
    <stop offset=".283" stopColor="#4F006F" stopOpacity="0" />
    <stop offset="1" stopColor="#4F006F" />
    </radialGradient>
    <linearGradient id="gword-h" x1="10.107" x2="21.944" y1="12.852" y2="13.029" gradientUnits="userSpaceOnUse">
    <stop stopColor="#9DEAFF" />
    <stop offset=".201" stopColor="#3BD5FF" />
    </linearGradient>
    <radialGradient id="gword-i" cx="0" cy="0" r="1" gradientTransform="matrix(-11.335 2.92732 -7.10275 -29.8256 21.946 9.18)" gradientUnits="userSpaceOnUse">
    <stop offset=".061" stopColor="#E4A7FE" />
    <stop offset=".539" stopColor="#E4A7FE" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="gword-j" cx="0" cy="0" r="1" gradientTransform="rotate(45 -21.228 10.207) scale(18.3848)" gradientUnits="userSpaceOnUse">
    <stop offset=".081" stopColor="#367AF2" />
    <stop offset=".872" stopColor="#001A8F" />
    </radialGradient>
    <radialGradient id="gword-k" cx="0" cy="0" r="1" gradientTransform="matrix(0 9.1 -10.3715 0 7.5 25.8)" gradientUnits="userSpaceOnUse">
    <stop offset=".587" stopColor="#2763E5" stopOpacity="0" />
    <stop offset=".974" stopColor="#58AAFE" />
    </radialGradient>
    </defs>
    </>
  ),
  /** Excel (.xlsx/.xls/.xlsm) */
  excel: (
    <>
    <path fill="url(#gexcel-a)" d="M2 8a3 3 0 0 1 3-3h19a6 6 0 0 1 6 6v13a3 3 0 0 1-3 3H8a6 6 0 0 1-6-6V8z" />
    <path fill="url(#gexcel-b)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gexcel-c)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gexcel-d)" d="M22 22.75c0 .69-.56 1.25-1.25 1.25H15v-3a4 4 0 0 0-4-4h-1v-3.125c0-1.036.84-1.875 1.875-1.875H22v10.75z" />
    <path fill="url(#gexcel-e)" fillOpacity=".7" d="M22 22.75c0 .69-.56 1.25-1.25 1.25H15v-3a4 4 0 0 0-4-4h-1v-3.125c0-1.036.84-1.875 1.875-1.875H22v10.75z" />
    <path fill="url(#gexcel-f)" d="M17.248 14c-.69 0-1.248.559-1.248 1.248v2a1.25 1.25 0 0 1-1.253 1.25l-.63-.003A3.991 3.991 0 0 0 11 17h-1v-1.125c0-1.036.84-1.875 1.875-1.875h5.373z" />
    <path fill="url(#gexcel-g)" fillOpacity=".3" d="M17.248 14c-.69 0-1.248.559-1.248 1.248v2a1.25 1.25 0 0 1-1.253 1.25l-.63-.003A3.991 3.991 0 0 0 11 17h-1v-1.125c0-1.036.84-1.875 1.875-1.875h5.373z" />
    <path fill="url(#gexcel-h)" d="M17.5 14h-5.156A2.344 2.344 0 0 0 10 16.344V11.5A2.5 2.5 0 0 1 12.5 9h5v5z" />
    <path fill="url(#gexcel-i)" d="M17.5 14h-5.156A2.344 2.344 0 0 0 10 16.344V11.5A2.5 2.5 0 0 1 12.5 9h5v5z" />
    <path fill="url(#gexcel-j)" d="M17.5 14h-5.156A2.344 2.344 0 0 0 10 16.344V11.5A2.5 2.5 0 0 1 12.5 9h5v5z" />
    <path fill="url(#gexcel-k)" d="M22 10.25C22 9.56 21.44 9 20.75 9h-3.5c-.69 0-1.25.56-1.25 1.25v2.5c0 .69.56 1.25 1.25 1.25h3.5c.69 0 1.25-.56 1.25-1.25v-2.5z" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gexcel-l)" rx="3" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gexcel-m)" fillOpacity=".3" rx="3" />
    <path fill="#fff" d="M10.723 28h-1.89l-1.187-2.23a6.86 6.86 0 0 1-.097-.186 6.566 6.566 0 0 1-.064-.166h-.02a1.466 1.466 0 0 1-.082.19 7.89 7.89 0 0 0-.093.18L6.06 28H4.277l2.139-3.505L4.424 21h1.865l1.055 1.992c.042.081.078.153.107.214.033.059.065.129.098.21h.02l.107-.224.117-.22L8.887 21h1.777l-2.021 3.441L10.723 28z" />
    <defs>
    <linearGradient id="gexcel-a" x1="11.042" x2="13.385" y1="5" y2="27.366" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gexcel-b" x1="10.222" x2="12.181" y1="6" y2="26.367" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gexcel-c" cx="0" cy="0" r="1" gradientTransform="matrix(13.7222 15.8333 -20.5833 5.9375 11.306 11.208)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <radialGradient id="gexcel-d" cx="0" cy="0" r="1" gradientTransform="matrix(-11.3333 -12.3333 9.54948 -8.77158 22 25)" gradientUnits="userSpaceOnUse">
    <stop offset=".065" stopColor="#379539" />
    <stop offset=".422" stopColor="#297C2D" />
    <stop offset=".703" stopColor="#15561C" />
    </radialGradient>
    <radialGradient id="gexcel-e" cx="0" cy="0" r="1" gradientTransform="matrix(-4.79167 -4.79167 3.71016 -3.70864 13.667 16.333)" gradientUnits="userSpaceOnUse">
    <stop stopColor="#073B10" />
    <stop offset=".992" stopColor="#084A13" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="gexcel-f" x1="8.077" x2="15.799" y1="19.679" y2="19.679" gradientUnits="userSpaceOnUse">
    <stop stopColor="#52D17C" />
    <stop offset=".329" stopColor="#4AA647" />
    </linearGradient>
    <linearGradient id="gexcel-g" x1="15.639" x2="15.639" y1="14" y2="21.89" gradientUnits="userSpaceOnUse">
    <stop stopColor="#29852F" />
    <stop offset=".5" stopColor="#4AA647" stopOpacity="0" />
    </linearGradient>
    <linearGradient id="gexcel-h" x1="10.276" x2="19.21" y1="17.798" y2="12.444" gradientUnits="userSpaceOnUse">
    <stop stopColor="#66D052" />
    <stop offset="1" stopColor="#85E972" />
    </linearGradient>
    <radialGradient id="gexcel-i" cx="0" cy="0" r="1" gradientTransform="matrix(-3.75784 0 0 -11.5142 17.23 13.621)" gradientUnits="userSpaceOnUse">
    <stop offset=".292" stopColor="#4EB43B" />
    <stop offset="1" stopColor="#72CC61" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="gexcel-j" x1="13.381" x2="10" y1="14.061" y2="14.061" gradientUnits="userSpaceOnUse">
    <stop offset=".184" stopColor="#C0E075" stopOpacity="0" />
    <stop offset="1" stopColor="#D1EB95" />
    </linearGradient>
    <radialGradient id="gexcel-k" cx="0" cy="0" r="1" gradientTransform="matrix(-5.96857 -5.69975 4.89508 -6.9497 21.969 14.7)" gradientUnits="userSpaceOnUse">
    <stop offset=".44" stopColor="#79E96D" />
    <stop offset="1" stopColor="#D0EB76" />
    </radialGradient>
    <radialGradient id="gexcel-l" cx="0" cy="0" r="1" gradientTransform="rotate(45 -21.228 10.207) scale(18.3848 52.3364)" gradientUnits="userSpaceOnUse">
    <stop stopColor="#20A85E" />
    <stop offset=".944" stopColor="#09442A" />
    </radialGradient>
    <radialGradient id="gexcel-m" cx="0" cy="0" r="1" gradientTransform="matrix(0 9.1 -10.4813 0 7.5 25.8)" gradientUnits="userSpaceOnUse">
    <stop offset=".58" stopColor="#33A662" stopOpacity="0" />
    <stop offset=".974" stopColor="#98F0B0" />
    </radialGradient>
    </defs>
    </>
  ),
  /** CSV */
  csv: (
    <>
    <path fill="url(#gcsv-a)" d="M2 8a3 3 0 0 1 3-3h19a6 6 0 0 1 6 6v13a3 3 0 0 1-3 3H8a6 6 0 0 1-6-6V8z" />
    <path fill="url(#gcsv-b)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gcsv-c)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gcsv-d)" d="M24.25 19.25A.75.75 0 0 1 25 20c0 .611-.007 1.279-.213 1.903-.22.668-.645 1.237-1.37 1.721a.75.75 0 1 1-.833-1.248c.474-.316.673-.622.778-.941.12-.363.138-.796.138-1.435a.75.75 0 0 1 .75-.75z" />
    <path fill="url(#gcsv-e)" d="M16.5 10c1.347 0 2.527.346 3.352 1.26.809.897 1.148 2.203 1.148 3.834v6.156a.75.75 0 0 1-1.5 0v-.291c-.8.6-1.942 1.041-3.5 1.041a4.88 4.88 0 0 1-1-.1V21c0-.23-.02-.454-.058-.673.299.11.65.173 1.058.173 1.457 0 2.334-.468 2.839-.939.259-.24.43-.49.535-.7a1.59 1.59 0 0 0 .106-.266c.02-.074.02-.105.02-.095v-2.432a2.114 2.114 0 0 1-.464-.17c-.138-.067-.293-.155-.427-.228-.536-.293-1.283-.67-2.609-.67-.984 0-1.575.425-1.947.96-.396.57-.553 1.287-.553 1.79 0 .045.003.088.004.132a3.986 3.986 0 0 0-1.46-.743c.09-.646.327-1.387.778-2.037C13.45 14.2 14.484 13.5 16 13.5c1.674 0 2.677.498 3.328.854l.148.079c-.08-1.071-.358-1.749-.738-2.17-.436-.483-1.132-.763-2.238-.763-.989 0-1.703.013-2.47.78a.75.75 0 0 1-1.06-1.06C14.203 9.986 15.489 10 16.5 10z" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gcsv-f)" rx="3" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gcsv-g)" fillOpacity=".3" rx="3" />
    <path fill="#fff" d="M10.723 28h-1.89l-1.187-2.23a6.86 6.86 0 0 1-.097-.186 6.566 6.566 0 0 1-.064-.166h-.02a1.466 1.466 0 0 1-.082.19 7.89 7.89 0 0 0-.093.18L6.06 28H4.277l2.139-3.505L4.424 21h1.865l1.055 1.992c.042.081.078.153.107.214.033.059.065.129.098.21h.02l.107-.224.117-.22L8.887 21h1.777l-2.021 3.441L10.723 28z" />
    <defs>
    <linearGradient id="gcsv-a" x1="11.042" x2="13.385" y1="5" y2="27.366" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gcsv-b" x1="10.222" x2="12.181" y1="6" y2="26.367" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gcsv-c" cx="0" cy="0" r="1" gradientTransform="matrix(13.7222 15.8333 -20.5833 5.9375 11.306 11.208)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gcsv-d" x1="29.792" x2="12.593" y1="30.5" y2="6.951" gradientUnits="userSpaceOnUse">
    <stop offset=".168" stopColor="#15561C" />
    <stop offset=".541" stopColor="#15561C" />
    <stop offset="1" stopColor="#297C2D" />
    </linearGradient>
    <linearGradient id="gcsv-e" x1="29.792" x2="12.593" y1="30.5" y2="6.951" gradientUnits="userSpaceOnUse">
    <stop offset=".168" stopColor="#15561C" />
    <stop offset=".541" stopColor="#15561C" />
    <stop offset="1" stopColor="#297C2D" />
    </linearGradient>
    <radialGradient id="gcsv-f" cx="0" cy="0" r="1" gradientTransform="rotate(45 -21.228 10.207) scale(18.3848 52.3364)" gradientUnits="userSpaceOnUse">
    <stop stopColor="#20A85E" />
    <stop offset=".944" stopColor="#09442A" />
    </radialGradient>
    <radialGradient id="gcsv-g" cx="0" cy="0" r="1" gradientTransform="matrix(0 9.1 -10.4813 0 7.5 25.8)" gradientUnits="userSpaceOnUse">
    <stop offset=".58" stopColor="#33A662" stopOpacity="0" />
    <stop offset=".974" stopColor="#98F0B0" />
    </radialGradient>
    </defs>
    </>
  ),
  /** PowerPoint (.pptx/.ppt/.pptm) */
  ppt: (
    <>
    <path fill="url(#gppt-a)" d="M2 8a3 3 0 0 1 3-3h19a6 6 0 0 1 6 6v13a3 3 0 0 1-3 3H8a6 6 0 0 1-6-6V8z" />
    <path fill="url(#gppt-b)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gppt-c)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#gppt-d)" d="M16 9a7 7 0 1 1-1 13.929V21a4 4 0 0 0-4-4H9.072A7 7 0 0 1 16 9z" />
    <path fill="url(#gppt-e)" d="M16 9a7 7 0 1 1-1 13.929V21a4 4 0 0 0-4-4H9.072A7 7 0 0 1 16 9z" />
    <path fill="url(#gppt-f)" fillOpacity=".5" d="M16 9a7 7 0 1 1-1 13.929V21a4 4 0 0 0-4-4H9.072A7 7 0 0 1 16 9z" />
    <path fill="url(#gppt-g)" d="M16 9a7 7 0 0 1 6.05 10.517l.112-.244c.693-1.538-.432-3.278-2.118-3.278h-2.796a1.253 1.253 0 0 1-1.253-1.255l.002-2.782c.001-1.688-1.742-2.814-3.28-2.12l-.207.095A6.965 6.965 0 0 1 16 9z" />
    <path fill="url(#gppt-h)" d="M16 9a7 7 0 0 1 6.05 10.517l.112-.244c.693-1.538-.432-3.278-2.118-3.278h-2.796a1.253 1.253 0 0 1-1.253-1.255l.002-2.782c.001-1.688-1.742-2.814-3.28-2.12l-.207.095A6.965 6.965 0 0 1 16 9z" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gppt-i)" rx="3" />
    <rect width="13" height="13" x="1" y="18" fill="url(#gppt-j)" fillOpacity=".3" rx="3" />
    <path fill="#fff" d="M7.019 25.72V28H5.44v-7h2.437c.872 0 1.536.19 1.992.571.46.38.689.945.689 1.694 0 .771-.258 1.373-.772 1.806-.511.433-1.198.65-2.06.65h-.708zm0-3.51v2.3h.659c.39 0 .691-.103.903-.308.212-.205.317-.5.317-.883 0-.355-.104-.628-.312-.82-.205-.192-.5-.288-.884-.288H7.02z" />
    <defs>
    <linearGradient id="gppt-a" x1="11.042" x2="13.385" y1="5" y2="27.366" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gppt-b" x1="10.222" x2="12.181" y1="6" y2="26.367" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gppt-c" cx="0" cy="0" r="1" gradientTransform="matrix(13.7222 15.8333 -20.5833 5.9375 11.306 11.208)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gppt-d" x1="15.126" x2="6.542" y1="8.554" y2="16.94" gradientUnits="userSpaceOnUse">
    <stop offset=".058" stopColor="#FF7F48" />
    <stop offset="1" stopColor="#E5495B" />
    </linearGradient>
    <radialGradient id="gppt-e" cx="0" cy="0" r="1" gradientTransform="matrix(-10.1505 9.93875 -17.0846 -16.7282 18.677 13.063)" gradientUnits="userSpaceOnUse">
    <stop offset=".152" stopColor="#AA1D2D" />
    <stop offset=".381" stopColor="#D12B18" stopOpacity=".44" />
    <stop offset=".602" stopColor="#FF3C00" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="gppt-f" cx="0" cy="0" r="1" gradientTransform="matrix(12.3042 -5.69055 10.5289 21.826 6.206 21.691)" gradientUnits="userSpaceOnUse">
    <stop offset=".407" stopColor="#FF66FB" />
    <stop offset="1" stopColor="#EA3D01" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="gppt-g" cx="0" cy="0" r="1" gradientTransform="matrix(6.39204 -7.32044 6.82218 5.68073 15.228 16.956)" gradientUnits="userSpaceOnUse">
    <stop stopColor="#FD6EF9" />
    <stop offset=".637" stopColor="#F94" />
    <stop offset=".852" stopColor="#FCC479" />
    </radialGradient>
    <radialGradient id="gppt-h" cx="0" cy="0" r="1" gradientTransform="matrix(-.72202 4.89412 -11.0683 -1.55717 14.592 9.924)" gradientUnits="userSpaceOnUse">
    <stop offset=".144" stopColor="#FF8D13" />
    <stop offset=".537" stopColor="#FF7F29" stopOpacity="0" />
    </radialGradient>
    <radialGradient id="gppt-i" cx="0" cy="0" r="1" gradientTransform="rotate(45 -21.229 10.205) scale(18.3848)" gradientUnits="userSpaceOnUse">
    <stop stopColor="#F8193E" />
    <stop offset=".939" stopColor="#920616" />
    </radialGradient>
    <radialGradient id="gppt-j" cx="0" cy="0" r="1" gradientTransform="matrix(0 9.1 -10.3594 0 7.498 25.8)" gradientUnits="userSpaceOnUse">
    <stop offset=".576" stopColor="#FFB055" stopOpacity="0" />
    <stop offset=".974" stopColor="#FFF2BE" />
    </radialGradient>
    </defs>
    </>
  ),
  /** 한컴 한글 (.hwp/.hwpx) — 직접 그린 것 */
  hwp: (
    <>
    <path fill="url(#ghwp-a)" d="M5 5a3 3 0 0 1 3-3h13a6 6 0 0 1 6 6v19a3 3 0 0 1-3 3H11a6 6 0 0 1-6-6V5z" />
    <path fill="url(#ghwp-b)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#ghwp-c)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#ghwp-d)" d="M12.5 8.5a2 2 0 0 1 2-2H18v11h-3.5a2 2 0 0 1-2-2v-7z" />
    <path fill="#0B4F9E" d="M18 6.5h3.5a2 2 0 0 1 2 2v.75H18V6.5z" />
    <path fill="#0082E6" d="M18 9.25h5.5V12H18V9.25z" />
    <path fill="#42ADFF" d="M18 12h5.5v2.75H18V12z" />
    <path fill="#9BD2F5" d="M18 14.75h5.5v.75a2 2 0 0 1-2 2H18v-2.75z" />
    <path fill="#000" fillOpacity=".12" d="M18 6.5h.9v11H18v-11z" />
    <rect width="13" height="13" x="1" y="18" fill="url(#ghwp-e)" rx="3" />
    <rect width="13" height="13" x="1" y="18" fill="url(#ghwp-f)" fillOpacity=".55" rx="3" />
    <rect width="1.1" height="1.75" x="6.95" y="19.8" fill="#fff" rx=".55" />
    <rect width="7.8" height="1.15" x="3.6" y="21.9" fill="#fff" rx=".575" />
    <circle cx="7.5" cy="26.4" r="2.55" stroke="#fff" strokeWidth="1.15" />
    <defs>
    <linearGradient id="ghwp-a" x1="12.104" x2="16.851" y1="2" y2="29.972" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="ghwp-b" x1="11.556" x2="15.786" y1="3" y2="29.035" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="ghwp-c" cx="0" cy="0" r="1" gradientTransform="matrix(10.5556 20.5833 -15.8333 7.71875 12.389 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="ghwp-d" x1="12.5" x2="18" y1="6.5" y2="17.5" gradientUnits="userSpaceOnUse">
    <stop stopColor="#4FB4FF" />
    <stop offset="1" stopColor="#2C9FF7" />
    </linearGradient>
    <radialGradient id="ghwp-e" cx="0" cy="0" r="1" gradientTransform="rotate(45 -21.228 10.207) scale(18.3848)" gradientUnits="userSpaceOnUse">
    <stop offset=".081" stopColor="#3BAAFF" />
    <stop offset=".872" stopColor="#00449E" />
    </radialGradient>
    <radialGradient id="ghwp-f" cx="0" cy="0" r="1" gradientTransform="matrix(0 9.1 -10.3715 0 7.5 25.8)" gradientUnits="userSpaceOnUse">
    <stop offset=".587" stopColor="#1E7FE0" stopOpacity="0" />
    <stop offset=".974" stopColor="#7FCBFF" />
    </radialGradient>
    </defs>
    </>
  ),
  /** PDF */
  pdf: (
    <>
    <path fill="url(#gpdf-a)" d="M5 5a3 3 0 0 1 3-3h13a6 6 0 0 1 6 6v19a3 3 0 0 1-3 3H11a6 6 0 0 1-6-6V5z" />
    <path fill="url(#gpdf-b)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gpdf-c)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gpdf-d)" d="M21.5 23a.5.5 0 0 1 0 1h-11a.5.5 0 0 1 0-1h11z" />
    <path fill="url(#gpdf-e)" d="M21.5 20a.5.5 0 0 1 0 1h-11a.5.5 0 0 1 0-1h11z" />
    <path fill="url(#gpdf-f)" d="M21.5 17a.5.5 0 0 1 0 1h-11a.5.5 0 0 1 0-1h11z" />
    <path fill="#D20112" fillRule="evenodd" d="M11.5 8c.247 0 .605.07.917.294.337.243.583.643.583 1.206s-.246.963-.583 1.206c-.312.224-.67.294-.917.294H11v1.5a.5.5 0 0 1-1 0v-4l.01-.1a.5.5 0 0 1 .49-.4h1zm-.5 2h.5a.62.62 0 0 0 .333-.105c.08-.058.167-.158.167-.395 0-.237-.087-.337-.167-.395A.62.62 0 0 0 11.5 9H11v1zm4.5-2c.593 0 1.218.176 1.702.595.496.429.798 1.073.798 1.905 0 .832-.302 1.476-.798 1.905-.484.42-1.11.595-1.702.595h-1a.5.5 0 0 1-.5-.5v-4l.01-.1a.5.5 0 0 1 .49-.4h1zm-.5 4h.5c.407 0 .782-.121 1.048-.35.253-.22.452-.576.452-1.15s-.199-.93-.452-1.15c-.266-.229-.64-.35-1.048-.35H15v3z" clipRule="evenodd" />
    <path fill="#D20112" d="M21.5 8a.5.5 0 0 1 0 1H20v1h1a.5.5 0 0 1 0 1h-1v1.5a.5.5 0 0 1-1 0v-4l.01-.1a.5.5 0 0 1 .49-.4h2z" />
    <defs>
    <linearGradient id="gpdf-a" x1="12.104" x2="16.851" y1="2" y2="29.972" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gpdf-b" x1="11.556" x2="15.786" y1="3" y2="29.035" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gpdf-c" cx="0" cy="0" r="1" gradientTransform="matrix(10.5556 20.5833 -15.8333 7.71875 12.389 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gpdf-d" x1="13.875" x2="14.431" y1="17" y2="24.151" gradientUnits="userSpaceOnUse">
    <stop stopColor="#898989" />
    <stop offset="1" stopColor="#636262" />
    </linearGradient>
    <linearGradient id="gpdf-e" x1="13.875" x2="14.431" y1="17" y2="24.151" gradientUnits="userSpaceOnUse">
    <stop stopColor="#898989" />
    <stop offset="1" stopColor="#636262" />
    </linearGradient>
    <linearGradient id="gpdf-f" x1="13.875" x2="14.431" y1="17" y2="24.151" gradientUnits="userSpaceOnUse">
    <stop stopColor="#898989" />
    <stop offset="1" stopColor="#636262" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 마크다운 */
  markup: (
    <>
    <path fill="url(#gmarkup-a)" fillRule="evenodd" d="M21 2a6 6 0 016 6v18.5a3.5 3.5 0 01-3.5 3.5h-17A3.5 3.5 0 015 23.337V5a3 3 0 013-3z" clipRule="evenodd" />
    <path fill="url(#gmarkup-b)" fillRule="evenodd" d="M6.5 29h14.55A3.5 3.5 0 0120 26.5c0-.98.402-1.865 1.05-2.5H6.5a2.5 2.5 0 000 5m0-6h17a.5.5 0 110 1 2.5 2.5 0 102.5 2.5V8a5 5 0 00-5-5H8a2 2 0 00-2 2v18.035Q6.245 23 6.5 23" clipRule="evenodd" />
    <path fill="url(#gmarkup-c)" fillRule="evenodd" d="M6.5 29h14.55A3.5 3.5 0 0120 26.5c0-.98.402-1.865 1.05-2.5H6.5a2.5 2.5 0 000 5m0-6h17a.5.5 0 110 1 2.5 2.5 0 102.5 2.5V8a5 5 0 00-5-5H8a2 2 0 00-2 2v18.035Q6.245 23 6.5 23" clipRule="evenodd" />
    <rect width="1" height="8" x="10" y="8" fill="#365376" rx=".5" />
    <rect width="1" height="5.245" x="9.827" y="8.397" fill="#365376" rx=".5" transform="rotate(-35.67 9.827 8.397)" />
    <rect width="1" height="5.254" x="15.385" y="7.802" fill="#365376" rx=".5" transform="rotate(35.67 15.385 7.802)" />
    <rect width="1" height="8" x="15" y="8" fill="#365376" rx=".5" />
    <path fill="url(#gmarkup-d)" d="M19.5 8a.5.5 0 01.5.5v5.793l1.146-1.146a.5.5 0 11.707.707l-2 2a.5.5 0 01-.707 0l-2-2a.5.5 0 01.707-.707L19 14.293V8.5a.5.5 0 01.5-.5" />
    <defs>
    <linearGradient id="gmarkup-a" x1="10.75" x2="15.121" y1="2" y2="30.098" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gmarkup-b" x1="10.111" x2="13.974" y1="3" y2="29.151" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <linearGradient id="gmarkup-d" x1="28.256" x2="11.738" y1="23.68" y2="19.566" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    <radialGradient id="gmarkup-c" cx="0" cy="0" r="1" gradientTransform="matrix(11.6111 20.5833 -17.4167 7.71875 11.028 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    </defs>
    </>
  ),
  /** HTML */
  html: (
    <>
    <path fill="url(#ghtml-a)" d="M2 8a3 3 0 0 1 3-3h19a6 6 0 0 1 6 6v13a3 3 0 0 1-3 3H8a6 6 0 0 1-6-6V8z" />
    <path fill="url(#ghtml-b)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#ghtml-c)" d="M3 8a2 2 0 0 1 2-2h19a5 5 0 0 1 5 5v13a2 2 0 0 1-2 2H8a5 5 0 0 1-5-5V8z" />
    <path fill="url(#ghtml-d)" fillRule="evenodd" d="M16 9a7 7 0 1 1 0 14 7 7 0 0 1 0-14zm1.71 1.247A6.015 6.015 0 0 1 21.197 13h-2.486c-.226-1.11-.573-2.058-1.001-2.753zm0 11.506A6.015 6.015 0 0 0 21.197 19h-2.486c-.226 1.11-.573 2.058-1.001 2.753zM17.689 19a8.851 8.851 0 0 1-.487 1.556c-.24.56-.5.948-.737 1.182-.233.23-.389.262-.465.262-.076 0-.232-.032-.465-.262-.238-.234-.498-.623-.737-1.182A8.851 8.851 0 0 1 14.31 19h3.378zm1.187-1h2.783A5.99 5.99 0 0 0 22 16a5.99 5.99 0 0 0-.341-2h-2.783c.08.634.124 1.305.124 2 0 .695-.043 1.366-.124 2zm-1.009-4c.086.623.133 1.295.133 2 0 .705-.047 1.377-.133 2h-3.734A14.772 14.772 0 0 1 14 16c0-.705.047-1.377.133-2h3.734zm-.178-1a8.851 8.851 0 0 0-.487-1.556c-.24-.56-.5-.948-.737-1.182-.233-.23-.389-.262-.465-.262-.076 0-.232.032-.465.262-.238.234-.498.623-.737 1.182-.191.446-.357.97-.487 1.556h3.378zm-4.4 0c.226-1.11.573-2.058 1.001-2.753A6.015 6.015 0 0 0 10.803 13h2.486zm-.165 1h-2.783A5.99 5.99 0 0 0 10 16c0 .701.12 1.374.341 2h2.783A15.86 15.86 0 0 1 13 16c0-.695.043-1.366.124-2zm.165 5h-2.486a6.015 6.015 0 0 0 3.487 2.753c-.428-.695-.775-1.644-1.001-2.753z" clipRule="evenodd" />
    <defs>
    <linearGradient id="ghtml-a" x1="11.042" x2="13.385" y1="5" y2="27.366" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="ghtml-b" x1="10.222" x2="12.181" y1="6" y2="26.367" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="ghtml-c" cx="0" cy="0" r="1" gradientTransform="matrix(13.7222 15.8333 -20.5833 5.9375 11.306 11.208)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="ghtml-d" x1="13.296" x2="17.325" y1="10.75" y2="22.833" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 코드·JSON */
  code: (
    <>
    <path fill="url(#gcode-a)" fillRule="evenodd" d="M21 2a6 6 0 0 1 6 6v18.5a3.5 3.5 0 0 1-3.5 3.5h-17A3.5 3.5 0 0 1 5 23.337V5a3 3 0 0 1 3-3h13z" clipRule="evenodd" />
    <path fill="url(#gcode-b)" fillRule="evenodd" d="M6.5 29h14.55A3.49 3.49 0 0 1 20 26.5c0-.98.402-1.865 1.05-2.5H6.5a2.5 2.5 0 0 0 0 5zm0-6h17a.5.5 0 1 1 0 1 2.5 2.5 0 1 0 2.5 2.5V8a5 5 0 0 0-5-5H8a2 2 0 0 0-2 2v18.035c.163-.023.33-.035.5-.035z" clipRule="evenodd" />
    <path fill="url(#gcode-c)" fillRule="evenodd" d="M6.5 29h14.55A3.49 3.49 0 0 1 20 26.5c0-.98.402-1.865 1.05-2.5H6.5a2.5 2.5 0 0 0 0 5zm0-6h17a.5.5 0 1 1 0 1 2.5 2.5 0 1 0 2.5 2.5V8a5 5 0 0 0-5-5H8a2 2 0 0 0-2 2v18.035c.163-.023.33-.035.5-.035z" clipRule="evenodd" />
    <path fill="url(#gcode-d)" d="M18.17 7.03a.5.5 0 0 1 .3.641l-4 11a.5.5 0 0 1-.94-.342l4-11a.5.5 0 0 1 .64-.299z" />
    <path fill="url(#gcode-e)" d="M11.354 10.147a.5.5 0 0 1 0 .707L9.207 13l2.147 2.147a.5.5 0 0 1-.708.707l-2.5-2.5a.5.5 0 0 1 0-.707l2.5-2.5a.5.5 0 0 1 .708 0z" />
    <path fill="url(#gcode-f)" d="M20.646 10.147a.5.5 0 0 1 .708 0l2.5 2.5a.5.5 0 0 1 0 .707l-2.5 2.5a.5.5 0 0 1-.708-.707L22.793 13l-2.147-2.146a.5.5 0 0 1 0-.707z" />
    <defs>
    <linearGradient id="gcode-a" x1="10.75" x2="15.121" y1="2" y2="30.098" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gcode-b" x1="10.111" x2="13.974" y1="3" y2="29.151" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gcode-c" cx="0" cy="0" r="1" gradientTransform="matrix(11.6111 20.5833 -17.4167 7.71875 11.028 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gcode-d" x1="11.5" x2="16.941" y1="5" y2="19" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    <linearGradient id="gcode-e" x1="11.5" x2="16.941" y1="5" y2="19" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    <linearGradient id="gcode-f" x1="11.5" x2="16.941" y1="5" y2="19" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 텍스트 (.txt) */
  text: (
    <>
    <path fill="url(#gtext-a)" d="M5 5a3 3 0 0 1 3-3h13a6 6 0 0 1 6 6v19a3 3 0 0 1-3 3H11a6 6 0 0 1-6-6V5z" />
    <path fill="url(#gtext-b)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gtext-c)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gtext-d)" fillRule="evenodd" d="M10 8.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z" clipRule="evenodd" />
    <defs>
    <linearGradient id="gtext-a" x1="12.104" x2="16.851" y1="2" y2="29.972" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gtext-b" x1="11.556" x2="15.786" y1="3" y2="29.035" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gtext-c" cx="0" cy="0" r="1" gradientTransform="matrix(10.5556 20.5833 -15.8333 7.71875 12.389 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gtext-d" x1="13.682" x2="19.379" y1="10" y2="22.811" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 이미지 */
  image: (
    <>
    <path fill="url(#gimage-a)" d="M3 6a3 3 0 0 1 3-3h17a6 6 0 0 1 6 6v17a3 3 0 0 1-3 3H9a6 6 0 0 1-6-6V6z" />
    <path fill="url(#gimage-b)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gimage-c)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gimage-d)" d="m14.94 15.06-5.38 5.38c-.944.944-.275 2.56 1.061 2.56H21.38c1.336 0 2.005-1.616 1.06-2.561l-5.38-5.379a1.5 1.5 0 0 0-2.12 0z" />
    <circle cx="21" cy="11" r="2" fill="url(#gimage-e)" />
    <defs>
    <linearGradient id="gimage-a" x1="11.396" x2="14.897" y1="3" y2="29.256" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gimage-b" x1="10.667" x2="13.703" y1="4" y2="28.287" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gimage-c" cx="0" cy="0" r="1" gradientTransform="matrix(12.6667 19 -19 7.125 11.667 10.25)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gimage-d" x1="9" x2="15.538" y1="11.5" y2="29.611" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    <linearGradient id="gimage-e" x1="19" x2="23.345" y1="6.5" y2="14.82" gradientUnits="userSpaceOnUse">
    <stop offset="0" stopColor="#597493" />
    <stop offset="1" stopColor="#0F2F56" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 동영상 */
  video: (
    <>
    <path fill="url(#gvideo-a)" d="M3 6a3 3 0 0 1 3-3h17a6 6 0 0 1 6 6v17a3 3 0 0 1-3 3H9a6 6 0 0 1-6-6V6z" />
    <path fill="url(#gvideo-b)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gvideo-c)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gvideo-d)" d="M13.235 10.195A1.5 1.5 0 0 0 11 11.502v8.996a1.5 1.5 0 0 0 2.235 1.307l8-4.498a1.5 1.5 0 0 0 0-2.614l-8-4.498z" />
    <defs>
    <linearGradient id="gvideo-a" x1="11.396" x2="14.897" y1="3" y2="29.256" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gvideo-b" x1="10.667" x2="13.703" y1="4" y2="28.287" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gvideo-c" cx="0" cy="0" r="1" gradientTransform="matrix(12.6667 19 -19 7.125 11.667 10.25)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gvideo-d" x1="16.5" x2="15.073" y1="19.063" y2="12.284" gradientUnits="userSpaceOnUse">
    <stop stopColor="#892FFF" />
    <stop offset="1" stopColor="#A966FF" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 오디오 */
  audio: (
    <>
    <path fill="url(#gaudio-a)" d="M3 6a3 3 0 0 1 3-3h17a6 6 0 0 1 6 6v17a3 3 0 0 1-3 3H9a6 6 0 0 1-6-6V6z" />
    <path fill="url(#gaudio-b)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gaudio-c)" d="M4 6a2 2 0 0 1 2-2h17a5 5 0 0 1 5 5v17a2 2 0 0 1-2 2H9a5 5 0 0 1-5-5V6z" />
    <path fill="url(#gaudio-d)" d="M14.25 9a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-1.5 0V9.75a.75.75 0 0 1 .75-.75z" />
    <path fill="url(#gaudio-e)" d="M18.25 12a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5a.75.75 0 0 1 .75-.75z" />
    <path fill="url(#gaudio-f)" d="M10.25 14a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75z" />
    <path fill="url(#gaudio-g)" d="M22.25 14a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75z" />
    <defs>
    <linearGradient id="gaudio-a" x1="11.396" x2="14.897" y1="3" y2="29.256" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gaudio-b" x1="10.667" x2="13.703" y1="4" y2="28.287" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gaudio-c" cx="0" cy="0" r="1" gradientTransform="matrix(12.6667 19 -19 7.125 11.667 10.25)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    <linearGradient id="gaudio-d" x1="16.25" x2="13.492" y1="19.573" y2="7.425" gradientUnits="userSpaceOnUse">
    <stop stopColor="#892FFF" />
    <stop offset="1" stopColor="#A966FF" />
    </linearGradient>
    <linearGradient id="gaudio-e" x1="16.25" x2="13.492" y1="19.573" y2="7.425" gradientUnits="userSpaceOnUse">
    <stop stopColor="#892FFF" />
    <stop offset="1" stopColor="#A966FF" />
    </linearGradient>
    <linearGradient id="gaudio-f" x1="16.25" x2="13.492" y1="19.573" y2="7.425" gradientUnits="userSpaceOnUse">
    <stop stopColor="#892FFF" />
    <stop offset="1" stopColor="#A966FF" />
    </linearGradient>
    <linearGradient id="gaudio-g" x1="16.25" x2="13.492" y1="19.573" y2="7.425" gradientUnits="userSpaceOnUse">
    <stop stopColor="#892FFF" />
    <stop offset="1" stopColor="#A966FF" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 압축 */
  archive: (
    <>
    <path fill="url(#garchive-a)" d="M11.525 5.025A3.5 3.5 0 0 0 9.05 4H5.5A3.5 3.5 0 0 0 2 7.5v16A3.5 3.5 0 0 0 5.5 27h21a3.5 3.5 0 0 0 3.5-3.5v-13A3.5 3.5 0 0 0 26.5 7H14.121a1.5 1.5 0 0 1-1.06-.44l-1.536-1.535z" />
    <path fill="url(#garchive-b)" fillOpacity=".2" d="M11.525 5.025A3.5 3.5 0 0 0 9.05 4H5.5A3.5 3.5 0 0 0 2 7.5v16A3.5 3.5 0 0 0 5.5 27h21a3.5 3.5 0 0 0 3.5-3.5v-13A3.5 3.5 0 0 0 26.5 7H14.121a1.5 1.5 0 0 1-1.06-.44l-1.536-1.535z" />
    <path fill="url(#garchive-c)" d="M3 12.5A2.5 2.5 0 0 1 5.5 10h5.25a2.5 2.5 0 0 0 2-1 2.5 2.5 0 0 1 2-1H26.5a2.5 2.5 0 0 1 2.5 2.5v13a2.5 2.5 0 0 1-2.5 2.5h-21A2.5 2.5 0 0 1 3 23.5v-11z" />
    <path fill="#944600" fillRule="evenodd" d="M14.501 15a3.5 3.5 0 1 1 0 7c-.424 0-5.713-.642-8.211-.947A1.466 1.466 0 0 1 5.059 20H7.5a1.5 1.5 0 0 0 0-3H5.059c.159-.554.633-.98 1.231-1.054 2.498-.305 7.787-.946 8.211-.946zm-.001 1a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z" clipRule="evenodd" />
    <path fill="#944600" d="M21.5 17a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5zm4 0a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5zm-6-1a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5zm4 0a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5zm4 0a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 1 .5-.5zm-20 2a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1h3z" />
    <defs>
    <linearGradient id="garchive-a" x1="9" x2="9" y1="26" y2="4" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BA7300" />
    <stop offset=".221" stopColor="#C77B00" />
    <stop offset=".716" stopColor="#D48300" />
    <stop offset="1" stopColor="#D48300" />
    </linearGradient>
    <radialGradient id="garchive-b" cx="0" cy="0" r="1" gradientTransform="matrix(0 2.92121 -10 0 12 10.5)" gradientUnits="userSpaceOnUse">
    <stop />
    <stop offset="1" stopOpacity="0" />
    </radialGradient>
    <linearGradient id="garchive-c" x1="14.25" x2="10.375" y1="26.25" y2="8" gradientUnits="userSpaceOnUse">
    <stop stopColor="#F7AB40" />
    <stop offset="1" stopColor="#FFDD57" />
    </linearGradient>
    </defs>
    </>
  ),
  /** 그 외 전부 */
  other: (
    <>
    <path fill="url(#gother-a)" d="M5 5a3 3 0 0 1 3-3h13a6 6 0 0 1 6 6v19a3 3 0 0 1-3 3H11a6 6 0 0 1-6-6V5z" />
    <path fill="url(#gother-b)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <path fill="url(#gother-c)" d="M6 5a2 2 0 0 1 2-2h13a5 5 0 0 1 5 5v19a2 2 0 0 1-2 2H11a5 5 0 0 1-5-5V5z" />
    <defs>
    <linearGradient id="gother-a" x1="12.104" x2="16.851" y1="2" y2="29.972" gradientUnits="userSpaceOnUse">
    <stop stopColor="#BBB" />
    <stop offset="1" stopColor="#888" />
    </linearGradient>
    <linearGradient id="gother-b" x1="11.556" x2="15.786" y1="3" y2="29.035" gradientUnits="userSpaceOnUse">
    <stop stopColor="#fff" />
    <stop offset="1" stopColor="#EBEBEB" />
    </linearGradient>
    <radialGradient id="gother-c" cx="0" cy="0" r="1" gradientTransform="matrix(10.5556 20.5833 -15.8333 7.71875 12.389 9.77)" gradientUnits="userSpaceOnUse">
    <stop offset=".177" stopColor="#fff" />
    <stop offset="1" stopColor="#EFEFEF" />
    </radialGradient>
    </defs>
    </>
  ),
};


/**
 * 유형별 viewBox — **크기를 같게 맞추는 자리** (2026-08-18).
 *
 * 원본 16종은 32 상자 안에서 저마다 다른 크기로 그려져 있다. 실제로 그려진 넓이를
 * 재 보면(브라우저 `getBBox`) 이렇다 — 왼쪽 위 좌표와 크기:
 *
 *   folder  2, 4,28,23   word   1, 2,26,29   excel  1, 5,29,26   csv   1, 5,29,26
 *   ppt     1, 5,29,26   hwp    1, 2,26,29   pdf    5, 2,22,28   markup 3, 2,24,28
 *   html    2, 5,28,22   code   3, 2,24,28   text   5, 2,22,28   image  3, 3,26,26
 *   video   3, 3,26,26   audio  3, 3,26,26   archive 2,4,28,23   other  5, 2,22,28
 *
 * 그대로 `0 0 32 32` 로 그리면 긴 변이 22~29 로 갈려 **같은 size 를 줘도 아이콘마다
 * 크기가 다르게 보인다**(배지가 붙은 Word·Excel 은 크고, 배지 없는 txt·PDF 는 작다).
 *
 * 그래서 유형마다 **그림에 맞춘 정사각 viewBox** 를 준다 — 그림의 한가운데를 상자
 * 한가운데에 두고, 긴 변이 상자의 28/32 을 차지하게 한다(원본 여백 비율 그대로).
 * 결과: 어떤 유형이든 긴 변 = `size × 0.875`, 그림 중심 = 상자 중심.
 *
 * ⚠ **svg 자체는 계속 정사각(size × size)** 이다. viewBox 만 바꾸므로 줄 안에서
 *   차지하는 자리는 유형과 무관하게 같다 — 이름 열의 시작점이 흔들리지 않는다.
 *
 * ⚠ 그림을 바꾸거나 더하면 **여기 값도 다시 재야 한다.** 안 재면 그 유형만 크거나
 *   작게 뜬다. 재는 법: 브라우저에서 `<svg viewBox="0 0 32 32">` 로 그린 뒤 defs 를
 *   뺀 자식들의 `getBBox()` 합집합 → `x+w/2 - S/2  y+h/2 - S/2  S  S`,
 *   `S = max(w,h) × 32/28`.
 */
const VIEW_BOX: Readonly<Record<FileGlyphName, string>> = {
  folder: '0 -0.5 32 32',
  word: '-2.571 -0.071 33.143 33.143',
  excel: '-1.071 1.429 33.143 33.143',
  csv: '-1.071 1.429 33.143 33.143',
  ppt: '-1.071 1.429 33.143 33.143',
  hwp: '-2.571 -0.071 33.143 33.143',
  pdf: '0 0 32 32',
  markup: '-1 0 32 32',
  html: '0 0 32 32',
  code: '-1 0 32 32',
  text: '0 0 32 32',
  image: '1.143 1.143 29.714 29.714',
  video: '1.143 1.143 29.714 29.714',
  audio: '1.143 1.143 29.714 29.714',
  archive: '0 -0.5 32 32',
  other: '0 0 32 32',
};

/** 그림 이름 전부 — 테스트·미리보기가 16종을 훑을 때 쓴다. */
export const FILE_GLYPH_NAMES = Object.keys(GLYPHS) as FileGlyphName[];

interface FileTypeGlyphProps {
  name: FileGlyphName;
  /** 한 변 픽셀 — 목록 16~19px, 타일 30px, 카드 22px. */
  size?: number;
}

/**
 * 유형 그림 하나를 그린다. 상자는 언제나 `size × size` 정사각이고, 그 안에서 그림이
 * 차지하는 크기는 유형과 무관하게 같다(위 `VIEW_BOX`).
 * `data-glyph` 는 테스트가 "어떤 그림이 골라졌는가" 를 확인하는 유일한 손잡이다.
 */
export function FileTypeGlyph({ name, size = 22 }: FileTypeGlyphProps) {
  return (
    <svg
      className="file-type-glyph"
      data-glyph={name}
      width={size}
      height={size}
      viewBox={VIEW_BOX[name]}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
    >
      {GLYPHS[name]}
    </svg>
  );
}
